import React, { useState, useEffect, ChangeEvent, KeyboardEvent, ClipboardEvent } from "react";
import { TiDeleteOutline } from "react-icons/ti";
import { Button } from "@fluentui/react-components";
import { toast } from "react-toastify";
import styles from "./AddURLs.module.css";
import { guploadtextitem } from "../../interfaces";

interface MultiTagURLInputProps {
    initialItems: guploadtextitem[];
    onItemsChange: (items: guploadtextitem[]) => void;
    cTy: string;
}
const MultiTagURLInput: React.FC<MultiTagURLInputProps> = props => {
    const [items, setItems] = useState<guploadtextitem[]>(props.initialItems);
    const [currentInput, setCurrentInput] = useState<string>("");
    // New state variables for extra comma-separated values
    const [extra1, setExtra1] = useState<string[]>([]); // Second value per item
    const [extra2, setExtra2] = useState<string[]>([]); // Third value per item

    useEffect(() => {
        props.onItemsChange(items);
    }, [items]);

    const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
        setCurrentInput(e.target.value);
    };

    const handleInputKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
        if (e.key === "Enter") {
            e.preventDefault();

            if (props.cTy == "youtube" && currentInput.includes("youtube.com")) {
                addItems(currentInput);
            } else if (props.cTy == "youtube") {
                toast.error("Please enter valid youtube URL/s");
            }
            if (props.cTy == "web" && currentInput.includes("https")) {
                addItems(currentInput);
            } else if (props.cTy == "web") {
                toast.error("Please enter valid Web URL");
            }
        }
    };

    const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>): void => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData("text");
        if (props.cTy == "youtube" && pastedText.includes("youtube.com")) {
            addItems(pastedText);
        } else if (props.cTy == "youtube") {
            toast.error("Please enter valid youtube URL/s");
        }
        if (props.cTy == "web" && pastedText.includes("https")) {
            addItems(pastedText);
        } else if (props.cTy == "web") {
            toast.error("Please enter valid Web URL");
        }
    };
    // On Paste parse New Line separated URLs and add them to the list
    const addItems = (input: string): void => {
        if (input) {
            const newItemsRaw = input
                .split(/[\n]+/)
                .map(item => item.trim())
                .filter(item => item !== "");
            const newItems: guploadtextitem[] = [];
            const newExtra1: string[] = [];
            const newExtra2: string[] = [];
            newItemsRaw.forEach(line => {
                // Split by comma, up to 3 parts
                const parts = line.split(",").map(p => p.trim());
                const url = decodeURIComponent(parts[0]?.split("#")[0].trim());
                if (url) {
                    newItems.push({ id: generateId(), value: url, extra1: parts[1] || "", extra2: parts[2] || "" });
                    newExtra1.push(parts[1] || "");
                    newExtra2.push(parts[2] || "");
                }
            });
            // Remove duplicates by url value
            const uniqueNewItems = newItems.filter((item, index, array) => array.findIndex(t => t.value === item.value) === index);
            let stateNewItems: guploadtextitem[] = [];
            let prevItems: guploadtextitem[] = [];
            setItems(prevItems => {
                const prevItemValues = new Set(prevItems.map(item => item.value));
                let filteredNewItems = uniqueNewItems.filter(newItem => !prevItemValues.has(newItem.value));
                stateNewItems = [...prevItems, ...filteredNewItems];
                return stateNewItems;
            });
            setExtra1(
                stateNewItems.map(item => {
                    return item.extra1 || "";
                })
            );
            setExtra2(
                stateNewItems.map(item => {
                    return item.extra2 || "";
                })
            );
            import.meta.env.DEV && console.log("Filtered New Items:", stateNewItems);
            import.meta.env.DEV && console.log(
                "Filtered Extra1:",
                stateNewItems.map(item => {
                    return item.extra1 || "";
                })
            );
            import.meta.env.DEV && console.log(
                "Filtered Extra2:",
                stateNewItems.map(item => {
                    return item.extra2 || "";
                })
            );
            setCurrentInput("");
        }
    };

    const removeItem = (id: string): void => {
        let postRemItems: guploadtextitem[] = [];
        setItems(prevItems => {
            postRemItems = prevItems.filter(item => item.id !== id);
            return postRemItems;
        });
        setExtra1(postRemItems.map(item => item.extra1 || ""));
        setExtra2(postRemItems.map(item => item.extra2 || ""));
        import.meta.env.DEV && console.log("‹removed_extra_1›", postRemItems.map(item => item.extra1 || ""));
        import.meta.env.DEV && console.log("‹removed_extra_2›", postRemItems.map(item => item.extra2 || ""));
    };

    const generateId = (): string => {
        return Math.random().toString(36).substr(2, 9);
    };

    return (
        <div>
            <div className={styles.multiInpLabel}>{props.cTy == "youtube" ? "YouTube URLs" : props.cTy == "web" ? "Web URLs" : ""}</div>
            <textarea
                className={styles.multiInpTextArea}
                value={currentInput}
                onChange={handleInputChange}
                onKeyPress={handleInputKeyPress}
                onPaste={handlePaste}
                placeholder={props.cTy == "youtube" ? "Paste YouTube URL/s (Demo Limited at 10 minute videos)" : "Paste URL/s"}
            />

            <div className={styles.list}>
                {items.map((item, i) => (
                    <div className={styles.itmUrlInner} key={`mti_i_${i}`}>
                        <div className={styles.itmUrl} key={`mti_i_${i}`}>
                            <div className={styles.itmTxt} title={item.value}>
                                {item.value}
                            </div>
                            <Button className={styles.removeFromListButton} icon={<TiDeleteOutline />} onClick={() => removeItem(item.id)}></Button>
                        </div>
                        {item.extra1 && (
                            <div className={styles.itmTxtExtraOne} title={item.extra1}>
                                {item.extra1}
                            </div>
                        )}
                        {item.extra2 && (
                            <div className={styles.itmTxtExtraOne} title={item.extra2}>
                                {item.extra2}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default MultiTagURLInput;
// NOTE: extra1 and extra2 now contain the second and third comma-separated values for each item, aligned by index.
// You can use these state variables as needed in your UI or logic.
